import React, { useEffect, useState } from "react";
import { adminApi, type PlaylistResolutionReport } from "../../../repositories/admin";
import Button from "../../../components/ui/Button";
import { formInputClass, formLabelClass, formHelpClass } from "../../../components/ui/formStyles";

/**
 * AD-306: the duplicate Amazon playlist resolution job, as Admin analytics shows it.
 *
 * The ticket asks for one thing here: the weekly count of groups re-checked because our stored
 * placements showed their merged copies disagreeing. The alert line beside it is the share above
 * which admins are emailed, editable without a deploy.
 */

const number = (value: number) => value.toLocaleString();

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const RUN_STATUS_LABELS: Record<string, string> = {
  running: "Running",
  complete: "Complete",
  capped: "Stopped at its budget; the next run carries on",
  failed: "Failed",
};

export const PlaylistResolutionCard: React.FC = () => {
  const [report, setReport] = useState<PlaylistResolutionReport | null>(null);
  const [alertPercent, setAlertPercent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = (next: PlaylistResolutionReport) => {
    setReport(next);
    setAlertPercent(String(next.disagreementAlertPercent));
  };

  useEffect(() => {
    adminApi
      .getPlaylistResolution()
      .then((res) => apply(res.data.data))
      .catch(() => setError("Could not load the Amazon playlist merge report."));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await adminApi.updatePlaylistResolution({
        disagreementAlertPercent: Number(alertPercent),
      });
      apply(res.data.data);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "The alert line was not saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!report) {
    return (
      <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
        <p className="text-xs text-neutral-500 sm:text-sm">Amazon playlist merges</p>
        {error ? (
          <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
        ) : (
          <p className="mt-1 text-lg font-bold text-black">…</p>
        )}
      </div>
    );
  }

  const { last7Days, weeklyDisagreements, lastRun } = report;

  return (
    <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
      <p className="text-xs text-neutral-500 sm:text-sm">Amazon playlist merges</p>

      {!report.jobEnabled && (
        <p className="mt-2 text-sm text-amber-700">
          The merge job is not running on this server. It runs with the playlist sync.
        </p>
      )}

      <div className="mt-4">
        <h4 className="text-sm font-semibold text-black">Disagreement re-checks</h4>
        <p className={formHelpClass}>
          Groups re-checked because our stored placements showed their merged copies at different
          positions on the same day. Each group counts once a week.
        </p>

        <div className="p-3 mt-3 rounded-md bg-neutral-50">
          <p className="text-xs text-neutral-500">Last 7 days</p>
          <p
            className={`mt-1 text-lg font-bold ${
              last7Days.overThreshold ? "text-red-600" : "text-black"
            }`}
          >
            {number(last7Days.groups)} of {number(last7Days.candidateGroups)} groups (
            {last7Days.percent}%)
          </p>
          <p className="text-xs text-neutral-500">
            Admins are emailed above {report.disagreementAlertPercent}%.
          </p>
        </div>

        <ul className="grid gap-2 mt-3 grid-cols-2 sm:grid-cols-4" aria-label="Weekly re-checks">
          {weeklyDisagreements.map((week) => (
            <li key={week.from} className="p-2 text-sm rounded-md bg-neutral-50">
              <span className="block text-xs text-neutral-500">
                {shortDate(week.from)} – {shortDate(week.to)}
              </span>
              <span className="font-semibold text-black">{number(week.groups)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6">
        <h4 className="text-sm font-semibold text-black">Last run</h4>
        {lastRun ? (
          <p className="mt-1 text-sm text-neutral-700">
            {new Date(lastRun.startedAt).toLocaleString()} ·{" "}
            {RUN_STATUS_LABELS[lastRun.status] || lastRun.status} ·{" "}
            {number(lastRun.apiCallsMade)} calls · {number(lastRun.merges)} merged ·{" "}
            {number(lastRun.mismatches)} kept apart · {number(lastRun.removed)} removed
          </p>
        ) : (
          <p className="mt-1 text-sm text-neutral-500">The job has not run yet.</p>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 mt-6">
        <div>
          <label htmlFor="disagreement-alert-percent" className={formLabelClass}>
            Alert line (%)
          </label>
          <input
            id="disagreement-alert-percent"
            type="number"
            min={0.1}
            max={100}
            step={0.1}
            className={formInputClass}
            value={alertPercent}
            onChange={(e) => {
              setSaved(false);
              setAlertPercent(e.target.value);
            }}
          />
        </div>
        <Button loading={saving} disabled={saving} onClick={save}>
          Save
        </Button>
        {saved && <p className="text-sm text-neutral-600">Saved. Used from the next run.</p>}
        {error && <p className="text-sm font-medium text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default PlaylistResolutionCard;
